import React, { ReactNode, useEffect } from "react";
import styled from "styled-components";
import { Portal } from "react-portal";
import { Transition } from "react-transition-group";
import { X } from "@sonder/ui/source/icons";
import {
  ModalClose as ModalCloseFragment,
  Modal as ModalFragment,
  ModalHeader as ModalHeaderFragment,
  ModalTitle,
  Overlay as OverlayFragment,
} from "@sonder/ui/source/styled/fragments/Modal";

interface ModalFunctionParams {
  closeModal: () => void;
  openModal: (event?: any) => void;
}

export interface ModalRenderFn {
  (props: ModalFunctionParams): ReactNode;
}

export interface ModalProps {
  children: ModalRenderFn;
  className?: string;
  isOpen?: boolean;
  onClose?(): void;
  onOpen?(): void;
}

interface AnimatedProps {
  openState: string;
}

export const MODAL_TRANSITION_SPEED = 100;

const AnimatedOverlay = styled(OverlayFragment)<AnimatedProps>`
  opacity: ${props => (props.openState === "entered" ? 1 : 0)};
  transition: opacity ${MODAL_TRANSITION_SPEED}ms ease-in-out;
`;

const AnimatedModal = styled(ModalFragment)<AnimatedProps>`
  transform: translateY(
    ${props => (props.openState === "entered" ? "0" : "3rem")}
  );
  transition: transform ${MODAL_TRANSITION_SPEED}ms ease-in-out;
`;

export const Modal = ({
  children,
  className,
  isOpen,
  onClose = () => undefined,
  onOpen = () => undefined,
}: ModalProps) => {
  const onKeyUp = (ev: KeyboardEvent) => {
    if (isOpen && ev.key === "Escape") onClose();
  };
  useEffect(() => {
    if (isOpen) document.addEventListener("keyup", onKeyUp, true);

    return () => {
      document.removeEventListener("keyup", onKeyUp);
    };
  }, [isOpen]);
  return (
    <Transition
      mountOnEnter
      unmountOnExit
      in={isOpen}
      timeout={MODAL_TRANSITION_SPEED}
    >
      {openState => (
        <Portal>
          <AnimatedOverlay
            className={className}
            openState={openState}
            onClick={onClose}
          >
            <AnimatedModal
              openState={openState}
              onClick={e => e.stopPropagation()}
            >
              {children({
                openModal: onOpen,
                closeModal: onClose,
              })}
            </AnimatedModal>
          </AnimatedOverlay>
        </Portal>
      )}
    </Transition>
  );
};

export interface ModalHeaderProps {
  children?: ReactNode;
  className?: string;
  onClose?(): void;
  title?: ReactNode;
}
export const ModalHeader = ({
  children,
  className,
  onClose,
  title,
}: ModalHeaderProps) => (
  <ModalHeaderFragment className={className}>
    {title && <ModalTitle>{title}</ModalTitle>}
    {children}
    {onClose && (
      <ModalCloseFragment backgroundVariant="light" onClick={onClose}>
        <X />
      </ModalCloseFragment>
    )}
  </ModalHeaderFragment>
);
